import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';

import {
  Icon,
  TableBuilderFactory,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableService,
} from '@web/shared/ui';
import { Route, RouteParam } from '@web/common';
import { Observable, of } from 'rxjs';
import { once } from '@web/utils';
import { WarehouseReleaseCollectionItem } from '../../models/warehouse-release-collection-item';
import { WarehouseReleaseCollectionFilter } from '../../models/warehouse-release-collection-filter';
import { WarehouseReleaseListItemAction } from '../../enums/warehouse-release-list-item-action';
import { WarehouseReleaseListAction } from '../../enums/warehouse-release-list-action';
import { WarehouseReleaseDataSourceService } from '../../services/warehouse-release-data-source.service';
import { WarehouseReleaseStatusProviderService } from '../../providers/warehouse-release-status-provider.service';
import { WarehouseReleaseListCustomCellTemplateComponent } from '../../components/warehouse-release-list-custom-cell-template/warehouse-release-list-custom-cell-template.component';

@Component({
  selector: 'app-warehouse-release-list',
  imports: [TableComponent, WarehouseReleaseListCustomCellTemplateComponent],
  templateUrl: './warehouse-release-list.component.html',
  styleUrl: './warehouse-release-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarehouseReleaseListComponent {
  /* prettier-ignore */
  private readonly tableBuilder = inject(TableBuilderFactory<WarehouseReleaseCollectionItem, WarehouseReleaseCollectionFilter, WarehouseReleaseListItemAction, never, WarehouseReleaseListAction>).create();
  private readonly table = inject(TableService);
  /* prettier-ignore */
  private readonly warehouseReleaseDataSource = inject(WarehouseReleaseDataSourceService);
  /* prettier-ignore */
  private readonly releaseStatusProvider = inject(WarehouseReleaseStatusProviderService);

  public readonly createReleaseVisibility = input<boolean>(false);

  protected readonly config = this.buildTable();

  private buildTable(): TableConfig<
    WarehouseReleaseCollectionItem,
    WarehouseReleaseCollectionFilter,
    WarehouseReleaseListItemAction,
    never,
    WarehouseReleaseListAction
  > {
    return this.tableBuilder
      .dataKey('id')
      .columns((builder) => [
        builder
          .initText('number')
          .filterKey('number')
          .sortKey('number')
          .filterable(true)
          .sortable(true)
          .label('Numer wydania')
          .build(),

        builder
          .initCustom('status')
          .width('10rem')
          .filterKey('status')
          .sortKey('status')
          .filterable(true)
          .sortable(true)
          .label('Status')
          .build(),

        builder
          .initDate('createdAt')
          .filterKey('createdAt')
          .sortKey('createdAt')
          .filterable(true)
          .sortable(true)
          .label('Data utworzenia')
          .build(),

        builder
          .initDate('releasedAt')
          .filterKey('releasedAt')
          .sortKey('releasedAt')
          .filterable(true)
          .sortable(true)
          .label('Data wydania')
          .build(),

        builder
          .initCustom('productNames')
          .filterKey('goodsNames')
          .sortKey('goodsNames')
          .width('70rem')
          .filterable(true)
          .sortable(true)
          .label('Nazwy towarów')
          .build(),
      ])
      .actions((builder) => [
        builder
          .initRouterLink(WarehouseReleaseListAction.Create)
          .routerLink((provider) =>
            provider.routerLink(Route.WarehouseReleaseCreate),
          )
          .label('Dodaj wydanie')
          .visibility(() => of(this.createReleaseVisibility()))
          .build(),
      ])
      .filters((builder) => [
        builder //
          .initText('number')
          .build(),

        builder //
          .initSelect('status')
          .dictionary(() => this.releaseStatusProvider.selectItems())
          .build(),

        builder //
          .initText('goodsNames')
          .build(),

        builder //
          .initDateRange('releasedAt')
          .build(),

        builder //
          .initDateRange('createdAt')
          .build(),
      ])
      .itemActions((builder) => [
        builder
          .initRouterLink(WarehouseReleaseListItemAction.Preview)
          .visibility((item) => item.canBeViewed)
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.WarehouseReleaseDetails, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .tooltip('Podgląd')
          .icon(Icon.Eye)
          .build(),

        builder
          .initRouterLink(WarehouseReleaseListItemAction.Edit)
          .visibility((item) => item.canBeEdited)
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.WarehouseReleaseEdit, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .tooltip('Edytuj')
          .icon(Icon.Pencil)
          .build(),

        builder
          .initCommand(WarehouseReleaseListItemAction.Approve)
          .visibility((item) => item.canBeApproved)
          .command((item) => this.approve(item))
          .tooltip('Zatwierdź')
          .icon(Icon.CheckCircle)
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad,
        ): Observable<TableDataPayload<WarehouseReleaseCollectionItem>> =>
          this.warehouseReleaseDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  private approve(item: WarehouseReleaseCollectionItem): void {
    this.warehouseReleaseDataSource
      .approveById(item.id)
      .pipe(once())
      .subscribe({
        next: () => this.refresh(),
      });
  }

  private refresh(): void {
    this.table.refresh();
  }
}
